import { ComponentProps } from './index'
import { H2, Text } from 'library/atoms/Typography'
import Flex from 'shared/library/atoms/Flex'
import styled from '@emotion/styled'
import Link from 'next/link'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import money from 'shared/helpers/money'
import { PrismicNextImage } from '@prismicio/next'
import { Card } from './index'
import { css } from '@emotion/react'

type SchedulableProps = {
  schedulable: Card
}

const ActivityRow = ({ schedulable }: SchedulableProps) => {
  const { locale } = useRouter() || {}
  const { t } = useTranslation('chargeable')

  const priceInPoundsIsWholeNumber = (schedulable.price / 100) % 1 === 0
  const price = money({
    pence: schedulable.price,
    currency: 'gbp',
    locale,
    wholeNumber: priceInPoundsIsWholeNumber
  })

  return (
    <SchedulableRow>
      <Link href={schedulable.href}>
        <SchedulableTitle>{schedulable.title}</SchedulableTitle>
        <Text>
          {t('from-price')} {price}
        </Text>
      </Link>
    </SchedulableRow>
  )
}

export const ActivityList = ({
  title,
  lede,
  linkText,
  linkHref,
  image,
  schedulables
}: ComponentProps) => {
  return (
    <StyledSection>
      <FlexImage>
        <StyledPrismicNextImage field={image} />
      </FlexImage>
      <FlexContent>
        <ContentHeader>
          <H2>{title}</H2>
          {lede ? lede : ''}
        </ContentHeader>

        {schedulables &&
          schedulables
            .slice(0, 5)
            .map(card => <ActivityRow key={card.title} schedulable={card} />)}
        {schedulables && linkHref && linkText ? (
          <MoreLink>
            <Text fontStyle="p2">
              <Link href={linkHref}>{linkText}</Link>
            </Text>
          </MoreLink>
        ) : (
          <></>
        )}
      </FlexContent>
    </StyledSection>
  )
}

const StyledSection = styled.section(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  gap: '50px',

  padding: theme.mixins.pxSpread([0, theme.space[5]]),
  ...theme.mixins.centeredContainer('xxxl')
}))

const FlexImage = styled(Flex)(({ theme }) => ({
  maxWidth: '30%',
  display: 'none',

  [theme.mediaQueries.sm]: {
    display: 'flex'
  }
}))

const FlexContent = styled(Flex)(() =>
  css({
    flexDirection: 'column'
  })
)

const StyledPrismicNextImage = styled(PrismicNextImage)(() => ({
  width: '100%',
  height: '100%'
}))

const ContentHeader = styled.div(({ theme }) => ({
  width: '100%',
  display: 'block',

  [theme.mediaQueries.lg]: {
    width: '75%'
  }
}))

const SchedulableRow = styled.div(() =>
  css({
    display: 'flex',
    flexDirection: 'column',
    padding: '10px 0',
    borderBottom: '1px solid #000'
  })
)

const MoreLink = styled.div(() =>
  css({
    display: 'flex',
    flexDirection: 'column',
    padding: '10px 0'
  })
)

const SchedulableTitle = styled(Text)(() =>
  css({
    fontWeight: 'bold'
  })
)
